import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { CorrectiveAction, ActionStatus, NewActionForm } from "../types";
import {
	calculateMaxPriority,
	calculatePriorityForNewNotStartedAction,
} from "../utils";

interface UseActionManagementProps {
	userId: string | undefined;
	correctiveActions: CorrectiveAction[];
	refetch: () => void;
}

export const useActionManagement = ({
	userId,
	correctiveActions,
	refetch,
}: UseActionManagementProps) => {
	const [updating, setUpdating] = useState(false);

	const ensureActionOwnedByUser = useCallback(
		async (actionId: string): Promise<boolean> => {
			if (!userId) return false;

			const { data: actionRow, error: actionError } = await supabase
				.from("corrective_actions")
				.select("reflection_id")
				.eq("id", actionId)
				.maybeSingle();

			if (actionError || !actionRow) {
				console.error(
					"Error fetching action for ownership check:",
					actionError,
				);
				return false;
			}

			const { data: reflectionRow, error: reflectionError } = await supabase
				.from("reflections")
				.select("user_id")
				.eq("id", actionRow.reflection_id)
				.maybeSingle();

			if (reflectionError || !reflectionRow) {
				console.error(
					"Error fetching parent reflection for ownership check:",
					reflectionError,
				);
				return false;
			}

			return reflectionRow.user_id === userId;
		},
		[userId],
	);

	const createAction = useCallback(
		async (formData: NewActionForm) => {
			if (
				!userId ||
				!formData.title.trim() ||
				formData.selectedReflectionIds.length === 0
			) {
				return false;
			}

			setUpdating(true);
			try {
				// Calculate priority to put new action at the top of "Not Started" list
				const newPriority =
					calculatePriorityForNewNotStartedAction(correctiveActions);

				await Promise.all(
					formData.selectedReflectionIds.map(async (reflectionId) => {
						const { error } = await supabase.from("corrective_actions").insert({
							reflection_id: reflectionId,
							title: formData.title.trim(),
							description: formData.description.trim(),
							status: "not_started",
							priority: newPriority,
						});

						if (error) throw error;
					}),
				);

				refetch();
				return true;
			} catch (error) {
				console.error("Error creating new action:", error);
				return false;
			} finally {
				setUpdating(false);
			}
		},
		[userId, correctiveActions, refetch],
	);

	const updateStatus = useCallback(
		async (actionId: string, newStatus: ActionStatus) => {
			setUpdating(true);
			try {
				const owned = await ensureActionOwnedByUser(actionId);
				if (!owned) {
					alert("Unauthorized: you do not own this action.");
					return false;
				}

				const { error } = await supabase
					.from("corrective_actions")
					.update({ status: newStatus })
					.eq("id", actionId);

				if (error) throw error;

				refetch();
				return true;
			} catch (error) {
				console.error("Error updating action status:", error);
				return false;
			} finally {
				setUpdating(false);
			}
		},
		[ensureActionOwnedByUser, refetch],
	);

	const updatePriority = useCallback(
		async (actionId: string, newPriority: number) => {
			setUpdating(true);
			try {
				const owned = await ensureActionOwnedByUser(actionId);
				if (!owned) {
					alert("Unauthorized: you do not own this action.");
					return false;
				}

				const { error } = await supabase
					.from("corrective_actions")
					.update({ priority: newPriority })
					.eq("id", actionId);

				if (error) throw error;

				refetch();
				return true;
			} catch (error) {
				console.error("Error updating action priority:", error);
				return false;
			} finally {
				setUpdating(false);
			}
		},
		[ensureActionOwnedByUser, refetch],
	);

	const updateAction = useCallback(
		async (
			actionId: string,
			updates: { title?: string; description?: string },
		) => {
			setUpdating(true);
			try {
				const owned = await ensureActionOwnedByUser(actionId);
				if (!owned) {
					alert("Unauthorized: you do not own this action.");
					return false;
				}

				const updateData: { title?: string; description?: string } = {};
				if (updates.title !== undefined) {
					updateData.title = updates.title.trim();
				}
				if (updates.description !== undefined) {
					updateData.description = updates.description.trim();
				}

				const { error } = await supabase
					.from("corrective_actions")
					.update(updateData)
					.eq("id", actionId);

				if (error) throw error;

				refetch();
				return true;
			} catch (error) {
				console.error("Error updating action:", error);
				return false;
			} finally {
				setUpdating(false);
			}
		},
		[ensureActionOwnedByUser, refetch],
	);

	const deleteAction = useCallback(
		async (actionId: string) => {
			setUpdating(true);
			try {
				const owned = await ensureActionOwnedByUser(actionId);
				if (!owned) {
					alert("Unauthorized: you do not own this action.");
					return false;
				}

				const { error } = await supabase
					.from("corrective_actions")
					.delete()
					.eq("id", actionId);

				if (error) throw error;

				refetch();
				return true;
			} catch (error) {
				console.error("Error deleting action:", error);
				return false;
			} finally {
				setUpdating(false);
			}
		},
		[ensureActionOwnedByUser, refetch],
	);

	return {
		updating,
		createAction,
		updateStatus,
		updatePriority,
		updateAction,
		deleteAction,
	};
};
